import { ComponentProps, FC, MutableRefObject } from 'react';
import { type Availability, isClosed } from 'utils/booking';
import { useTranslation } from 'utils/i18n';
import BookingSlots from '../bookingSlots';
import type { ScrollAreaHandle } from '../scrollArea';
import ClosedSlot from './closedSlot';
import Slots from './slots';

interface OpeningTimes {
  [key: string]: string;
}

interface Closure {
  startDate: string;
  endDate: string;
}

// Optional fields tolerate the narrower objects some callers hand in;
// isClosed treats missing opening times as "not closed".
interface Space {
  id: number;
  openingTimes?: OpeningTimes;
  priceIncVat?: number;
  clinical?: boolean;
}

interface Venue {
  id?: number;
  openingTimes?: OpeningTimes;
  closures?: Closure[];
}

interface BookingItem {
  id?: number;
  startTime: string;
  endTime: string;
}

interface TimeSlot {
  startTime: string;
  endTime: string;
}

interface Props {
  date: string;
  clinical?: boolean;
  venue: Venue;
  space: Space;
  times: string[];
  startTime: string;
  endTime: string;
  slots?: ComponentProps<typeof BookingSlots>['slots'];
  bookingItems?: BookingItem[];
  scrollRef: MutableRefObject<ScrollAreaHandle>;
  availability?: Availability;
  loading?: boolean;
  disabled?: boolean;
  value?: TimeSlot[];
  onChange?: (value: TimeSlot[]) => void;
  setTouched?: (value: boolean) => void;
}

const emptyList: ComponentProps<typeof BookingSlots>['slots'] = [];

const SlotSelection: FC<Props> = ({
  date,
  venue,
  space,
  clinical = space.clinical,
  times,
  startTime,
  endTime,
  slots = emptyList,
  scrollRef,
  availability = {},
  loading = false,
  ...props
}) => {
  const { t } = useTranslation('common');

  if (loading) return null;

  if (
    !startTime ||
    !endTime ||
    isClosed(date, venue.openingTimes, venue.closures) ||
    isClosed(date, space.openingTimes)
  )
    return <ClosedSlot length={times.length || undefined} />;

  if (clinical && slots.length === 0)
    return (
      <ClosedSlot
        length={times.length || undefined}
        label={t('quick_book.timeslot_widget.unavailable')}
      />
    );

  return clinical ? (
    <BookingSlots
      {...props}
      slots={slots}
      showSlotTimes={false}
      availability={availability}
      spaces={[space]}
      openingTime={startTime}
      horizontal
      relativeSizeAndPosition
    />
  ) : (
    <Slots
      {...props}
      date={date}
      startTime={startTime}
      endTime={endTime}
      availability={availability}
      scale={2}
      times={times}
      horizontal
      scrollRef={scrollRef}
    />
  );
};

export default SlotSelection;
